import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CountUp } from "@/components/CountUp";
import { api, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, TrendingUp, Wallet, ShoppingCart, Building2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { t } from "@/lib/i18n";

export const COMPANY_COLORS = ["#16A34A", "#2563EB", "#F59E0B", "#DB2777", "#0891B2", "#7C3AED", "#DC2626"];
const MONTHS = [t("Gen"), t("Feb"), t("Mar"), t("Apr"), t("Mag"), t("Giu"), t("Lug"), t("Ago"), t("Set"), t("Ott"), t("Nov"), t("Dic")];

function Kpi({ icon: Icon, label, value, testid, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
      className="bg-card border border-border rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono-j uppercase tracking-wider text-muted-foreground">{label}</span>
        <div className="h-9 w-9 rounded-xl grid place-items-center bg-primary/15 text-primary"><Icon className="h-[18px] w-[18px]" /></div>
      </div>
      <p className="font-display text-3xl font-extrabold mt-4">{value}</p>
    </motion.div>
  );
}

function CompanyCard({ c, color, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05 }}
      className="bg-card border border-border rounded-2xl p-5 relative overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200" data-testid={`agent-company-card-${c.company_id}`}>
      <span className="absolute left-0 top-0 h-full w-1.5" style={{ background: color }} />
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4" style={{ color }} />
        <p className="font-semibold truncate">{c.company_name}</p>
      </div>
      <p className="font-display text-2xl font-extrabold mt-3" data-testid={`agent-company-revenue-${c.company_id}`}>{eur(c.revenue)}</p>
      <div className="flex justify-between text-xs text-muted-foreground mt-2">
        <span>{c.orders} {t("ordini")}</span>
        <span data-testid={`agent-company-commission-${c.company_id}`}>{t("Provv.")} {c.commission_rate}% · <b className="text-foreground">{eur(c.commission)}</b></span>
      </div>
    </motion.div>
  );
}

export function AgentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const { data } = useQuery({
    queryKey: ["agent-overview", year],
    queryFn: async () => (await api.get("/agent/overview", { params: { year } })).data,
  });
  const companies = data?.companies || [];
  const monthly = (data?.monthly || []).map((m) => ({ ...m, label: MONTHS[Number(m.month.slice(5)) - 1] }));

  return (
    <div className="space-y-6" data-testid="agent-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono-j uppercase tracking-wider text-muted-foreground">{t("Benvenuto,")} {user?.name?.split(" ")[0]}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">{t("Resoconto")} {year}</h1>
        </div>
        <div className="flex gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="agent-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[now, now - 1, now - 2, now - 3].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => navigate("/orders/new")} className="gap-2 font-semibold" data-testid="dash-new-order-btn">
            <Plus className="h-4 w-4" /> {t("Nuovo ordine")}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Kpi i={0} icon={TrendingUp} label={t("Fatturato annuo")} value={<CountUp value={data?.total_revenue} format={eur} />} testid="agent-total-revenue" />
        <Kpi i={1} icon={Wallet} label={t("Provvigioni maturate")} value={<CountUp value={data?.total_commission} format={eur} />} testid="agent-total-commission" />
        <Kpi i={2} icon={ShoppingCart} label={t("Ordini")} value={<CountUp value={data?.total_orders} />} testid="agent-total-orders" />
      </div>

      <div>
        <h3 className="font-semibold mb-3">{t("Fatturato per azienda")}</h3>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {companies.map((c, i) => <CompanyCard key={c.company_id} c={c} i={i} color={COMPANY_COLORS[i % COMPANY_COLORS.length]} />)}
          {!companies.length && <p className="text-sm text-muted-foreground">{t("Nessuna azienda collegata.")}</p>}
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5">
        <h3 className="font-semibold mb-4">{t("Andamento mensile per azienda")}</h3>
        <div className="h-[300px]" data-testid="agent-monthly-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} width={56} />
              <Tooltip formatter={(v, n) => [eur(v), companies.find((c) => c.company_id === n)?.company_name || n]} />
              <Legend formatter={(v) => companies.find((c) => c.company_id === v)?.company_name || v} />
              {companies.map((c, i) => (
                <Bar key={c.company_id} dataKey={c.company_id} stackId="a" fill={COMPANY_COLORS[i % COMPANY_COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t("Sono conteggiati solo gli ordini confermati ed evasi.")}</p>
    </div>
  );
}
